import React from 'react';

import { mfaMessageSets } from '@shipstation/client-common/mfa';

import { useEnableClientCommonStepUpAuth } from '../../../feature-flags/hooks/use-enable-client-common-step-up-auth';
import { StepUpAuthContextProvider as UntypedLegacyStepUpAuthContextProvider } from '../index';
import { messageSets } from '../messages';
import { StepUpAuthContextProvider as ClientCommonStepUpAuthContextProvider } from './step-up-auth-provider';

import type { PhoneVerificationState } from '@shipstation/client-common/mfa';
import type { ComponentProps, FC } from 'react';
import type { MessageDescriptor } from 'react-intl';

/**
 * `../index.jsx` under-declares two props in JSDoc: `phoneVerificationState` as the *keys* of
 * `PHONE_VERIFICATION_STATES` when every caller passes a value, and `children` as a single
 * element when callers pass conditional nodes. Both are stale annotations rather than behaviour
 * differences — the runtime strings are identical to the adapter's — and `index.jsx` is frozen
 * apart from the shared-context extraction, so correct the two here.
 */
type LegacyStepUpAuthContextProviderProps = Omit<
  ComponentProps<typeof UntypedLegacyStepUpAuthContextProvider>,
  'children' | 'phoneVerificationState'
> & {
  children?: React.ReactNode;
  phoneVerificationState?: PhoneVerificationState;
};

const LegacyStepUpAuthContextProvider = UntypedLegacyStepUpAuthContextProvider as FC<LegacyStepUpAuthContextProviderProps>;

export const MESSAGE_SET_KEYS = {
  mfaSettings: 'mfaSettings',
  lastMfaClaim: 'lastMfaClaim',
} as const;

export type MessageSetKey = (typeof MESSAGE_SET_KEYS)[keyof typeof MESSAGE_SET_KEYS];

const LEGACY_MESSAGE_SETS_BY_KEY = {
  [MESSAGE_SET_KEYS.mfaSettings]: messageSets.mfaSettingsMessages,
  [MESSAGE_SET_KEYS.lastMfaClaim]: messageSets.lastMfaClaimMessages,
};

const CLIENT_COMMON_MESSAGE_SETS_BY_KEY = {
  [MESSAGE_SET_KEYS.mfaSettings]: mfaMessageSets.mfaSettingsMessages,
  [MESSAGE_SET_KEYS.lastMfaClaim]: mfaMessageSets.lastMfaClaimMessages,
};

export type StepUpAuthProviderSwitchProps = {
  /** rendered only once step-up auth has completed */
  children?: React.ReactNode;
  /** request url that failed due to mfa required, or a USER_ACTIONS feature name */
  source: string;
  isModalOpen: boolean;
  /** title referencing the workflow gated by this step-up auth */
  stepUpAuthModalTitle?: MessageDescriptor;
  /**
   * Which catalog to use, not the catalog itself: the two providers take incompatible message
   * objects, so each branch resolves this key against its own catalog.
   */
  messageSetKey?: MessageSetKey;
  onClose?: () => void;
  onStepUpAuthSuccess?: (accessToken: string) => void;
  /**
   * Determines some of the properties on the token asked for during step-up auth. When false,
   * a token with an updated `last_mfa_completed` claim is requested instead.
   */
  isMfaSettings?: boolean;
  /** hides every close affordance — the user cannot skip this step-up */
  isUnskippable?: boolean;
  phoneVerificationState?: PhoneVerificationState;
  onTokenTimeout?: () => void;
};

/**
 * Selects between the legacy step-up auth provider and the client-common adapter based on
 * `enable-client-common-step-up-auth`.
 *
 * Both providers own the same responsibility — drive the step-up modal and publish the
 * resulting access token on `StepUpAuthContext` — so exactly one may be mounted at a time or a
 * single workflow would raise two modals.
 *
 * Feature flags resolve asynchronously from Redux, so this starts on the legacy provider and
 * swaps once the flag arrives. That window closes long before a user can open a step-up
 * workflow, and whichever provider is mounted at the time handles it end to end.
 *
 * Delete this switch and mount the adapter directly once the flag reaches 100%.
 */
export const StepUpAuthProviderSwitch = ({
  children,
  source,
  isModalOpen,
  stepUpAuthModalTitle,
  messageSetKey = MESSAGE_SET_KEYS.mfaSettings,
  onClose,
  onStepUpAuthSuccess,
  isMfaSettings = false,
  isUnskippable = false,
  phoneVerificationState,
  onTokenTimeout,
}: StepUpAuthProviderSwitchProps) => {
  const isClientCommonStepUpAuthEnabled = useEnableClientCommonStepUpAuth();

  if (isClientCommonStepUpAuthEnabled) {
    return (
      <ClientCommonStepUpAuthContextProvider
        isMfaSettings={isMfaSettings}
        isModalOpen={isModalOpen}
        isUnskippable={isUnskippable}
        messages={CLIENT_COMMON_MESSAGE_SETS_BY_KEY[messageSetKey]}
        onClose={onClose}
        onStepUpAuthSuccess={onStepUpAuthSuccess}
        onTokenTimeout={onTokenTimeout}
        phoneVerificationState={phoneVerificationState}
        source={source}
        stepUpAuthModalTitle={stepUpAuthModalTitle}
      >
        {children}
      </ClientCommonStepUpAuthContextProvider>
    );
  }

  return (
    <LegacyStepUpAuthContextProvider
      isMfaSettings={isMfaSettings}
      isModalOpen={isModalOpen}
      isUnskippable={isUnskippable}
      messages={LEGACY_MESSAGE_SETS_BY_KEY[messageSetKey]}
      onClose={onClose}
      onStepUpAuthSuccess={onStepUpAuthSuccess}
      onTokenTimeout={onTokenTimeout}
      phoneVerificationState={phoneVerificationState}
      source={source}
      stepUpAuthModalTitle={stepUpAuthModalTitle}
    >
      {children}
    </LegacyStepUpAuthContextProvider>
  );
};
